import { useLocation } from "wouter";
import { AnalyticsRouteSkeleton } from "./analyticsUi";

const Block = ({ className }: { className: string }) => <div className={`skeleton ${className}`} />;

const SquareGrid = ({ count, withCaption, size }: { count: number; withCaption?: boolean; size?: string }) => (
  <div className={`skeleton-square-grid${withCaption ? " has-captions" : ""}${size ? ` size-${size}` : ""}`}>
    {Array.from({ length: count }, (_, index) => (
      <div key={index}>
        <Block className="skeleton-square" />
        {withCaption && <><Block className="skeleton-caption" /><Block className="skeleton-caption is-short" /></>}
      </div>
    ))}
  </div>
);

const storedTileSize = (): string => {
  try {
    const value = localStorage.getItem("booth-control-gallery-tile-size");
    return value === "s" || value === "l" ? value : "m";
  } catch {
    return "m";
  }
};

/** The Home section heading and tile grid, placed exactly where the real tiles appear. */
export const HomeTilesSkeleton = ({ count = 16 }: { count?: number }) => (
  <div className="skeleton-section" aria-hidden="true">
    <Block className="skeleton-heading" />
    <SquareGrid count={count} withCaption />
  </div>
);

/** The gallery manager header, summary strip, tabs and photo grid. */
export const GallerySkeleton = () => (
  <div className="skeleton-gallery" aria-hidden="true">
    <Block className="skeleton-title" />
    <Block className="skeleton-meta" />
    <div className="skeleton-stats">{[0, 1, 2, 3].map((index) => <Block key={index} className="" />)}</div>
    <Block className="skeleton-tabs" />
    <SquareGrid count={21} size={storedTileSize()} />
  </div>
);

/** Placeholder shaped like the page being opened, shown while its code or sign-in loads. */
export const RouteSkeleton = () => {
  const [location] = useLocation();
  const page = location === "/"
    ? "home"
    : location.startsWith("/galleries")
      ? "gallery"
      : location.startsWith("/analytics")
        ? "analytics"
        : "page";
  return (
    <div className="route-skeleton" role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">Loading.</span>
      {page === "home" ? (
        <div className="skeleton-home" aria-hidden="true">
          <div className="skeleton-row"><Block className="skeleton-search" /><Block className="skeleton-filter" /></div>
          <HomeTilesSkeleton />
        </div>
      ) : page === "gallery" ? (
        <GallerySkeleton />
      ) : page === "analytics" ? (
        <AnalyticsRouteSkeleton />
      ) : (
        <PageSkeleton />
      )}
    </div>
  );
};

/** A page title, summary line and content panel. */
export const PageSkeleton = () => (
  <div className="skeleton-page" aria-hidden="true">
    <Block className="skeleton-title" />
    <Block className="skeleton-meta" />
    <Block className="skeleton-panel" />
  </div>
);

/** Sidebar and page placeholder for the moment sign-in is being restored. */
export const AppShellSkeleton = () => (
  <div className="app-shell-skeleton">
    <aside aria-hidden="true">
      <Block className="skeleton-brand" />
      {Array.from({ length: 9 }, (_, index) => <Block className="skeleton-nav" key={index} />)}
    </aside>
    <main>
      <RouteSkeleton />
    </main>
  </div>
);
